import { PeaksInstance, PlayerAdapter, Segment, WaveformZoomView } from "peaks.js"
import { emitEvent } from "@/services/event-emitter.ts"
import Konva from "konva"
import { EventTypes, ExplorerItemId } from "@/types/types.ts"
import { clamp } from "lodash"
import { getClientX } from "@/utils/utils.ts";
import { FileProgress } from "@uppy/utils/lib/FileProgress";
import Stage = Konva.Stage;
import KonvaEventObject = Konva.KonvaEventObject;
import Shape = Konva.Shape;
import Layer = Konva.Layer;
import Text = Konva.Text;

export const getMaxZoomLevel = (peaksInstance: PeaksInstance) => {
  return getZoomview(peaksInstance)._getScale(
    peaksInstance.player.getDuration()
  )
}

// SCROLL ZOOM
// pointerTime = getClientXWaveformTime(peaks, event: MouseEvent)
// - getClientXWaveformTime = zoomview.pixelOffsetToTime(getClientX(event))
// - getClientX = event.clientX
// pointerOffset = getOffsetFromTime(peaks, pointerTime)
// - getOffsetFromTime = timeToPixelOffset(pointerTime)
// - pointerTime = pointer time in waveform
export const focusScrollPositionAtPointer = (
  peaksInstance: PeaksInstanceType,
  pointerTime: number,
  pointerOffset: number
) => {
  const zoomview = getZoomview(peaksInstance)

  // @note: pointerTime and pointerOffset are fixed numbers (pixels) at this point (except when the mouse moves again).
  // The zoomview pixelsToTime function converts the pointerOffset pixels to time (seconds), relative to the zoomlevel
  zoomview.setStartTime(
    pointerTime - zoomview.pixelsToTime(pointerOffset)
  )
}

export const adjustZoom = (peaksInstance: PeaksInstance, adjustmentFactor: number) => {
  if (peaksInstance) {
    const zoomview = peaksInstance.views.getView("zoomview") as WaveformZoomviewType;
    if (zoomview) {
      const duration = peaksInstance.player.getDuration();
      // const currentSecondsPerPixel = calculateSecondsPerPixel(zoomview);

      // Dynamische grenzen instellen
      const minScale = 64; // Minimaal schaalniveau (64 pixels per seconde)
      const maxScale = zoomview._getScale(duration); // Maximaal schaalniveau op basis van bestandsgrootte

      const currentScale = zoomview._scale;
      const newScale = Math.max(currentScale * adjustmentFactor, 256);

      // Grenzen toepassen
      const clampedScale = Math.min(Math.max(newScale, minScale), maxScale);

      // Controleer of de schaal daadwerkelijk verandert
      if (clampedScale !== currentScale) {
        zoomview.setZoom({ scale: clampedScale });

        // const updatedSecondsPerPixel = calculateSecondsPerPixel(zoomview);
        // console.log(`Nieuwe schaal: ${clampedScale}, seconden per pixel: ${updatedSecondsPerPixel}`);
      }
    }
  }
};

export const scrollHorizontalByWheel = (peaksInstance: PeaksInstance, e: WheelEvent) => {
  const zoomview = getZoomview(peaksInstance)
  const delta = e.deltaY;
  zoomview.setStartTime(zoomview.pixelsToTime(zoomview._frameOffset) - delta / 100)
}

export const getMaximumZoomOutLevel = (peaksInstance: PeaksInstance) => {
  return getZoomview(peaksInstance)._getScale(peaksInstance.player.getDuration())
}

export const zoomFull = (peaksInstance: PeaksInstance) => {
  if (!peaksInstance) {
    return
  }

  peaksInstance.views.getView(WAVEFORM_ZOOMVIEW_TYPE_NAME)?.setZoom({
    scale: "auto",
  })
}

export const zoomIn = (peaksInstance: PeaksInstanceType) => {
  /* const zoomview = getZoomview(peaksInstance)
  const scale = zoomview._scale / 4;
  const newScale = zoomview._scale - scale; */
  const zoomview = getZoomview(peaksInstance)
  const maxScale = zoomview._getScale(peaksInstance.player.getDuration())
  let minScale = zoomview._scale - 120 * 16
  minScale = minScale <= 256 ? 256 : minScale

  const newScale = clamp(minScale, 64, maxScale)
  zoomview?.setZoom({
    scale: newScale,
  })
  emitEvent(EventTypes.ZOOM_CHANGED, { scale: peaksInstance.zoom.getZoom() })
}

export const zoomOut = (peaksInstance: PeaksInstanceType) => {
  const zoomview = getZoomview(peaksInstance)
  if (!zoomview) {
    return
  }
  const maxScale = zoomview._getScale(peaksInstance.player.getDuration())
  let minScale = zoomview._scale + 120 * 16
  minScale = minScale <= 256 ? 256 : minScale

  const newScale = clamp(minScale, 64, maxScale)
  zoomview?.setZoom({
    scale: newScale,
  })
  emitEvent(EventTypes.ZOOM_CHANGED, { scale: peaksInstance.zoom.getZoom() })
}

export const getPeaksInstance = (peaksInstance: PeaksInstance) => {
  return peaksInstance as PeaksInstance & {
    views: {
      _scrollbar: WaveformScrollbar
      _peaks: PeaksInstance
    }
  }
}

export type WaveformScrollbar = {
  _scrollboxWidth: number,
  _scrollbox: Shape
  _updateWaveform: (offset: number) => void
}

export type WaveformZoomviewType = {
  _stage: Stage
  _container: HTMLDivElement,
  _axisLayer: Layer,
  _createMouseDragHandler: () => void,
  _playheadLayer: {
    _playheadText: Text,
    _playheadLayer: Layer,
    _playheadPixel: number,
    _playheadColor: string,
    _playheadLine: {
      attrs: {
        stroke: string
        strokeWidth: number
      }
    },
  },
  _highlightLayer: Shape & { _highlightRect: Shape }
  _mouseDragHandler: {
    _mouseDragHandler: {
      _dragging: boolean
      _mouseDown: (e: KonvaEventObject<MouseEvent>) => void
      _mouseMove: (e: KonvaEventObject<MouseEvent>) => void
      _mouseUp: (e: KonvaEventObject<MouseEvent>) => void
    }
  }
  timeToPixels: (time: number) => number
  pixelsToTime: (pixels: number) => number
  pixelOffsetToTime: (offset: number) => number
  timeToPixelOffset: (offset: number) => number
  _scale: number
  _width: number
  _pixelLength: number
  _frameOffset: number
  _getScale: (duration: number) => number
  _data: {
    scale: number
    length: number
    sample_rate: number
  },
  _segmentsLayer: {
    _segmentShapes: {
      [key: string]: {
        _moveToTop: () => null
      }
    }
  }
} & WaveformZoomView

export type UploadingTrackType = {
  id: ExplorerItemId,
  progress: FileProgress
}

export type PeaksInstanceType = {
  options: {
    mediaElement?: Element;
  }
  player: {
    _adapter: PlayerAdapter & {
      _duration: number,
      _audio: HTMLAudioElement
    }
    play: () => Promise<void>
    pause: () => void
    getCurrentTime: () => number
    getDuration: () => number
    seek: (time: number) => void
    playSegment: (segment: Segment, loop?: boolean) => Promise<void>
  }
} & PeaksInstance

export const WAVEFORM_COMMENTS_CONTAINER_CLASSNAME = "comments-container";
export const WAVEFORM_ZOOMVIEW_CONTAINER_CLASSNAME = "zoomview-container";
export const WAVEFORM_OVERVIEW_CONTAINER_CLASSNAME = "overview-container";
export const WAVEFORM_SCROLLBAR_CONTAINER_CLASSNAME = "scrollbar-container";

export const WAVEFORM_ZOOMVIEW_TYPE_NAME = "zoomview";
export const WAVEFORM_OVERVIEW_TYPE_NAME = "overview";

/*export type WaveformOverviewType = {
  _stage: Stage
  _mouseDragHandler: {
    _mouseDragHandler: {
      _mouseDown: (e: KonvaEventObject<MouseEvent>) => void
    }
  }
} & WaveformOverview*/

export const getZoomview = (peaksInstance: PeaksInstanceType | PeaksInstance) => {
  return peaksInstance?.views.getView(WAVEFORM_ZOOMVIEW_TYPE_NAME) as WaveformZoomviewType
}
export const getClientXWaveformTime = (peaksInstance: PeaksInstanceType | PeaksInstance, event: MouseEvent) => {
  return getZoomview(peaksInstance)?.pixelOffsetToTime(getClientX(event))
}
export const getOffsetFromTime = (peaksInstance: PeaksInstanceType | PeaksInstance, time: number) => {
  return getZoomview(peaksInstance)?.timeToPixelOffset(time)
}
export const enableZoomviewSeek = (zoomview: WaveformZoomviewType) => {
  zoomview.enableSeek(true)
}
export const disableZoomViewSeek = (zoomview: WaveformZoomviewType) => {
  zoomview.enableSeek(false)
}
export const enableSegmentDragging = (zoomview: WaveformZoomviewType) => {
  zoomview.enableSegmentDragging(true)
}
export const disableSegmentDragging = (zoomview: WaveformZoomviewType) => {
  zoomview.enableSegmentDragging(false)
}